"use client";
import { DownloadIcon, FileText, Trash2, Eye } from "lucide-react";
import { Attachment } from "@PN/tabela";
import { Button } from "visuais";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "visuais";
import { useState } from "react";
import { URL } from "api";
import { toast } from "react-hot-toast";
import Image from "next/image";

// =================================================================
// Tipos
// =================================================================

interface AnexoListWithPreviewProps {
  anexos: Attachment[];
  onDownload: (filename: string) => void;
  onDelete: (filename: string) => void;
  onPreview?: (anexo: Attachment) => void;
  isLoading?: boolean;
}

// =================================================================
// Componente AnexoItem
// =================================================================

interface AnexoItemProps {
  anexo: Attachment;
  onDownload: (filename: string) => void;
  onDelete: (filename: string) => void;
  onPreview?: (anexo: Attachment) => void;
}

function AnexoItem({ anexo, onDownload, onDelete, onPreview }: AnexoItemProps) {
  return (
    <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" title={anexo.Z07_DESC}>
          {anexo.Z07_DESC}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {anexo.Z07_PATH}
        </p>
      </div>

      <div className="flex items-center gap-1 ml-2">
        {onPreview && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPreview(anexo)}
            className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
            title="Visualizar arquivo"
          >
            <Eye className="h-3 w-3" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDownload(anexo.Z07_PATH)}
          className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700"
          title="Baixar arquivo"
        >
          <DownloadIcon className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(anexo.Z07_PATH)}
          className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
          title="Excluir arquivo"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

// =================================================================
// Componente Principal
// =================================================================

export function AnexoListWithPreview({
  anexos,
  onDownload,
  onDelete,
  onPreview,
  isLoading = false,
}: AnexoListWithPreviewProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [fileSelecionado, setFileSelecionado] = useState<{
    url: string;
    name: string;
    type: string;
  } | null>(null);

  const handlePreview = async (anexo: Attachment) => {
    try {
      // Se for uma URL blob, abrir diretamente em nova aba
      if (anexo.Z07_PATH.startsWith("blob:")) {
        window.open(anexo.Z07_PATH, "_blank");
        return;
      }

      // ✅ CORREÇÃO: Fazer igual ao download - baixar como blob
      const response = await fetch(
        `${URL.in.API_APPS}/uploads/${anexo.Z07_PATH}`,
        {
          method: "GET",
          headers: { Accept: "*/*" },
        }
      );

      if (!response.ok) {
        throw new Error(`Erro ao carregar arquivo: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Determinar o tipo do arquivo
      const extension = anexo.Z07_PATH.split(".").pop()?.toLowerCase();
      let fileType = "application/octet-stream";

      if (extension === "pdf") {
        fileType = "application/pdf";
      } else if (["png", "jpg", "jpeg", "gif"].includes(extension || "")) {
        fileType = `image/${extension}`;
      }

      setFileSelecionado({
        url: blobUrl, // ✅ URL blob criada
        name: anexo.Z07_DESC,
        type: fileType,
      });
      setIsPreviewOpen(true);
    } catch (error) {
      console.error("Erro ao abrir preview:", error);
      toast.error("Erro ao abrir o arquivo para visualização");
    }
  };

  const handlePreviewClick = (anexo: Attachment) => {
    if (onPreview) {
      onPreview(anexo);
    } else {
      handlePreview(anexo);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground">
            Anexos Existentes
          </h4>
          <div className="flex items-center justify-center p-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-xs text-muted-foreground">Carregando...</p>
            </div>
          </div>
        </div>
      );
    }

    if (anexos.length === 0) {
      return (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground">
            Anexos Existentes
          </h4>
          <div className="flex items-center justify-center p-4">
            <div className="text-center">
              <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">
                Nenhum anexo encontrado
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-muted-foreground">
          Anexos Existentes ({anexos.length})
        </h4>
        <div className="max-h-48 overflow-y-auto border rounded-md p-2 bg-muted/10">
          <div className="space-y-2">
            {anexos.map((attachment: Attachment, index: number) => (
              <AnexoItem
                key={`${attachment.Z07_PATH}-${index}`}
                anexo={attachment}
                onDownload={onDownload}
                onDelete={onDelete}
                onPreview={handlePreviewClick}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderContent()}

      {fileSelecionado && (
        <Dialog
          open={isPreviewOpen}
          onOpenChange={() => {
            setIsPreviewOpen(false);
            setFileSelecionado(null);
          }}
        >
          <DialogContent className="min-w-[60vw] h-[90vh]">
            <DialogHeader>
              <DialogTitle>{fileSelecionado.name}</DialogTitle>
            </DialogHeader>

            {fileSelecionado.type.startsWith("image/") && (
              <Image
                src={fileSelecionado.url}
                alt={fileSelecionado.name}
                className="h-full w-full object-contain mx-auto"
              />
            )}

            {fileSelecionado.type === "application/pdf" && (
              <iframe
                src={fileSelecionado.url}
                className="w-full h-full border"
                title={fileSelecionado.name}
              />
            )}

            {!fileSelecionado.type.startsWith("image/") &&
              fileSelecionado.type !== "application/pdf" && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Tipo de arquivo não suportado para preview
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {fileSelecionado.name}
                    </p>
                  </div>
                </div>
              )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
